import { useCallback, useEffect, useState } from 'react'
import type { CostCentre } from './types'
import { _getCostCentre } from 'state/finance-viilutalous-actions'
import { objectToCamelCased } from 'util/changeCase'

/**
 * IMPORTANT NOTE: costCentreIds should be memoized so we don't fetch infinitely
 * @param params - The parameters for the hook.
 * @param params.costCentreIds - An array of cost centre IDs to fetch.
 * @param params.databaseSource - The database source identifier to query from.
 */
function useCostCentresByIds({
	databaseSource,
	companyUUID,
	costCentreIds,
}: {
	costCentreIds: number[]
	companyUUID: string | undefined
	databaseSource: string | undefined
}) {
	const [costCentres, setCostCentres] = useState<CostCentre[]>([])

	const [loading, setLoading] = useState<boolean>(false)
	const [error, setError] = useState<string | null>(null)

	const doQuery = useCallback(async () => {
		if (!companyUUID || !databaseSource) return
		setLoading(true)
		setError(null)
		let data: CostCentre[] | null = null

		try {
			const results = await Promise.all(
				costCentreIds.map((costCentreId) =>
					_getCostCentre({
						companyUUID,
						costCentreId,
						databaseSource,
					}),
				),
			)

			const jsons = await Promise.all(
				results.filter((r) => r && r.ok).map((r) => r?.json()),
			)

			data = jsons.map((json) => objectToCamelCased(json))
		} catch (err) {
			console.error('Error fetching costCentres:', err)
			setError(
				'An error occurred while fetching costCentres: ' + databaseSource,
			)
		}
		setCostCentres(data ?? [])
		setLoading(false)
	}, [companyUUID, databaseSource, costCentreIds])

	useEffect(() => {
		doQuery()
	}, [doQuery])

	return {
		costCentres,
		loading,
		error,
		refresh: doQuery,
	}
}

export type useCostCentresByIdsResult = ReturnType<typeof useCostCentresByIds>

export default useCostCentresByIds
